/**
 * activityKindView — how the Activity page reads an event's KIND (P3, 2026-09-16).
 *
 * Every platform-summoned turn used to write `source: 'prest0n-session'`, so a
 * dev-cycle check-in, a routine run and a person typing all rendered as "chat
 * task" with a raw member id beside them, and there was no way to ask the page
 * "what has Prest0n been doing on his own?". Rows now carry a `kind`.
 *
 * TWO readings, and the fallback is the load-bearing half: a row written before
 * today has NO kind, and classifying it by its `source` is what keeps history
 * in the filter instead of vanishing from it.
 *
 * Firebase-free by construction — pure functions over a row shape.
 */
import { isBackgroundExpenseSource } from './usageVocabulary';

/** The filter value the page sends for "everything Prest0n did on his own". */
export const PREST0N_OWN_WORK_FILTER = '!user-turn';

export interface ActivityKindRow {
  kind?: string | null;
  source?: string | null;
}

/**
 * The kind to READ for a row, declared or inferred. An undeclared row is a
 * person's turn unless its source says it was background work — exactly the
 * classification the expenses list has used since 2026-09-10.
 */
export function effectiveActivityKind(row: ActivityKindRow): string {
  const declared = String(row?.kind || '').trim();
  if (declared) return declared;
  return isBackgroundExpenseSource(row?.source) ? 'sweep' : 'user-turn';
}

/** Did a PERSON ask for this and wait for it? */
export function isPersonsOwnTurn(row: ActivityKindRow): boolean {
  return effectiveActivityKind(row) === 'user-turn';
}

/**
 * Whose name belongs in the member column. Prest0n's own work is HIS, however
 * the row's memberId reads: the acting uid on a check-in is the agent's, which
 * rendered as a truncated raw id nobody could identify.
 */
export function activityActorLabel(row: ActivityKindRow, memberName: string | null | undefined): string | null {
  if (!isPersonsOwnTurn(row)) return 'Prest0n';
  return memberName ?? null;
}

/** The one-word badge a non-user row wears, or null for a person's turn. */
export function activityKindBadge(row: ActivityKindRow): string | null {
  switch (effectiveActivityKind(row)) {
    case 'user-turn': return null;
    case 'routine': return 'routine';
    case 'platform-summon': return 'Prest0n';
    case 'coding-run': return 'coding';
    case 'decision': return 'routing';
    case 'note': return 'note';
    default: return 'background';
  }
}

/**
 * P3: which machinery posted a platform note, in the words a founder uses.
 * The `sourceTag` is the write's own tag (`triage_report`, `triage_hub`,
 * `routine_state_change`, …) — the ⓘ names it instead of saying only that no
 * compute was needed, which was true and told the reader nothing.
 *
 * An unrecognised tag returns '' so the popup falls back to the plain sentence
 * rather than printing a raw slug at somebody.
 */
export function platformNoteSweepLabel(sourceTag?: string | null): string {
  switch (String(sourceTag || '').trim()) {
    case 'triage_report': return "from the Triage Center's report sweep";
    case 'triage_occurrence': return 'because this issue was seen again';
    case 'triage_hub': return "from the engineering hub's health check";
    case 'triage_ask_nudge': return 'because a decision has been waiting';
    case 'routine_state_change': return 'because a routine changed state';
    case 'routine_welcome': return 'when this routine was created';
    default: return '';
  }
}
